
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setAnalyticsUserId, setAnalyticsUserProperties, trackAccountSignupCompleted, trackAdsSignupConversion } from "@/lib/analytics";
import { trackAffiliateConversion, getAffiliateCookie } from "@/lib/affiliate-tracker";

declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
    klaviyo?: { identify: (props: Record<string, unknown>) => void; push: (args: unknown[]) => void };
  }
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  session: null,
  isLoading: true
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // Update Reddit Pixel with advanced matching when user logs in
        if (session?.user?.email && window.rdt) {
          window.rdt('init', 'a2_igw3yy71c5sc', {
            email: session.user.email,
            externalId: session.user.id,
          });
        }

        // Identify user in Klaviyo for marketing emails
        if (session?.user?.email && window.klaviyo) {
          const userName = session.user.user_metadata?.full_name || session.user.user_metadata?.name;
          window.klaviyo.identify({
            email: session.user.email,
            external_id: session.user.id,
            ...(userName ? { first_name: userName.split(' ')[0], last_name: userName.split(' ').slice(1).join(' ') } : {}),
          });
        }

        // Subscribe user to Klaviyo list via edge function on login/signup
        if (session?.user?.email) {
          const userName = session.user.user_metadata?.full_name || session.user.user_metadata?.name;

          // Fetch plan info from profiles table
          supabase
            .from('profiles')
            .select('has_lifetime_access, subscription_active_until, is_in_trial, trial_ends_at')
            .eq('id', session.user.id)
            .single()
            .then(({ data: profile }) => {
              let plan = 'Free';
              if (profile?.has_lifetime_access) {
                plan = 'Lifetime';
              } else if (profile?.subscription_active_until && new Date(profile.subscription_active_until) > new Date()) {
                plan = 'Active Subscriber';
              } else if (profile?.is_in_trial && profile?.trial_ends_at && new Date(profile.trial_ends_at) > new Date()) {
                plan = 'Trial';
              }

              supabase.functions.invoke('klaviyo-subscribe', {
                body: { email: session.user.email, userId: session.user.id, fullName: userName, plan },
              }).catch(() => {
                // Non-critical — don't block auth flow
              });

              // GA4: Set user properties based on profile data
              const signupMethod = (session.user.app_metadata?.provider === 'google' ? 'google' : 'email') as 'google' | 'email';
              let accountState: 'no_trial' | 'trial_active' | 'trial_expired' = 'no_trial';
              let planType: 'none' | 'monthly' | 'yearly' = 'none';

              if (profile?.has_lifetime_access) {
                accountState = 'trial_active'; // lifetime = active
                planType = 'yearly'; // closest match
              } else if (profile?.subscription_active_until && new Date(profile.subscription_active_until) > new Date()) {
                accountState = 'trial_active';
              } else if (profile?.is_in_trial && profile?.trial_ends_at && new Date(profile.trial_ends_at) > new Date()) {
                accountState = 'trial_active';
              } else if (profile?.trial_ends_at) {
                accountState = 'trial_expired';
              }

              setAnalyticsUserProperties({
                signup_method: signupMethod,
                account_state: accountState,
                plan_type: planType,
                trial_started: !!(profile?.trial_ends_at || profile?.is_in_trial),
              });

              // Count flows for user property
              supabase
                .from('flows')
                .select('id', { count: 'exact', head: true })
                .eq('user_id', session.user.id)
                .then(({ count }) => {
                  setAnalyticsUserProperties({ flows_created: count ?? 0 });
                })
                .catch(() => {});
            })
            .catch(() => {
              // Fallback: send without plan info
              supabase.functions.invoke('klaviyo-subscribe', {
                body: { email: session.user.email, userId: session.user.id, fullName: userName },
              }).catch(() => {});
            });
        }

        // GA4: Set user ID and detect first-time signups (Google or email).
        // For email signups, the first SIGNED_IN event fires after the user
        // clicks the verification link — so we use this as the authoritative
        // "signup completed" signal and also fire affiliate conversion here.
        // This is later than the raw signup event, but it only credits the
        // affiliate for verified, real users.
        if (session?.user) {
          setAnalyticsUserId(session.user.id);

          const isFirstSignInThisSession = prevUserIdRef.current === null;
          const createdAt = new Date(session.user.created_at);
          const isRecentSignup = (Date.now() - createdAt.getTime()) < 5 * 60_000; // within 5 min

          if (isFirstSignInThisSession && isRecentSignup) {
            const provider = session.user.app_metadata?.provider === 'google' ? 'google' : 'email';

            // Only track Google signup here — email signup (both the GA4 event
            // and the Google Ads conversion) is fired at the submit moment in
            // Auth.tsx. Google OAuth has no separate submit moment, so the
            // verified return IS the signup submission.
            if (provider === 'google') {
              trackAccountSignupCompleted('google');

              // Google Ads signup conversion. Pass email for enhanced
              // conversions (Google hashes client-side).
              trackAdsSignupConversion(session.user.email);
            }

            // Affiliate conversion — fire for both providers at verified
            // first-login so affiliates only get credit for real users.
            const affiliateCode = getAffiliateCookie();
            if (affiliateCode) {
              // Persist attribution to the profile for the paid-conversion
              // event later (fired from the autumn-webhooks handler when
              // the user activates a paid subscription).
              supabase
                .from('profiles')
                .update({ affiliate_code: affiliateCode })
                .eq('id', session.user.id)
                .is('affiliate_code', null)
                .then(() => {/* non-critical */});

              trackAffiliateConversion({
                userId: session.user.email || session.user.id,
                orderId: `signup_${session.user.id}`,
                amount: 0,
                planName: 'Free Signup',
                planInterval: 'lifetime',
                affiliateCode,
              }).catch(() => {
                // Non-critical
              });
            }
          }

          prevUserIdRef.current = session.user.id;
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
